import { MediaMatcher } from '@angular/cdk/layout';
import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { Subject, ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { User, UserRole } from 'src/app/models/User';
import { AdminService } from 'src/app/services/admin.service';
import { UserService } from 'src/app/services/user.service';
import { environment } from 'src/environments/environment';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { EventLogItemType, EventLogSource } from 'src/app/models/EventLog';
import { CountryCode, COUNTRY_CODES } from 'src/app/models/country-codes';

@Component({
  selector: 'app-new-user',
  templateUrl: './new-user.component.html',
  styleUrls: ['./new-user.component.css'],
})
export class NewUserComponent implements OnInit {
  private ngUnsubscribe = new Subject();
  private e164Regex = /^\+[1-9]\d{1,14}$/;

  countryCodes: CountryCode[] = COUNTRY_CODES;
  selectedCountry: CountryCode;

  countryFilterCtrl: UntypedFormControl = new UntypedFormControl();
  filteredCountryCodes: ReplaySubject<CountryCode[]> = new ReplaySubject<CountryCode[]>(1);

  Form = new UntypedFormGroup({
    firstname: new UntypedFormControl('', [Validators.required]),
    lastname: new UntypedFormControl('', [Validators.required]),
    initials: new UntypedFormControl('', [Validators.required]),
    role: new UntypedFormControl('', [Validators.required]),
    title: new UntypedFormControl('', [Validators.required]),
    email: new UntypedFormControl('', [Validators.required]),
    countryCode: new UntypedFormControl(null, [Validators.required]),
    phonenumber: new UntypedFormControl('', [Validators.required]),
  }, { validators: this.e164PhoneValidator() });
  error: string;

  message = 'Laster opp...';

  get firstname() {
    return this.Form.get('firstname');
  }
  get lastname() {
    return this.Form.get('lastname');
  }
  get initials() {
    return this.Form.get('initials');
  }
  get role() {
    return this.Form.get('role');
  }
  get title() {
    return this.Form.get('title');
  }
  get email() {
    return this.Form.get('email');
  }
  get phonelocale() {
    return this.Form.get('countryCode');
  }
  get countryCode() {
    return this.Form.get('countryCode');
  }
  get phonenumber() {
    return this.Form.get('phonenumber');
  }

  roles: UserRole[] = [];

  mobileQuery: MediaQueryList;

  constructor(
    private userservice: UserService,
    private adminservice: AdminService,
    media: MediaMatcher,
    private dialogRef: MatDialogRef<NewUserComponent>,
    private afs: AngularFirestore,
    private snackbarservice: SnackbarService
  ) {
    adminservice
      .getUserRoles()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((roles: UserRole[]) => {
        this.roles = roles;
      });
    this.mobileQuery = media.matchMedia(environment.mobileQueryMaxwidthSmall);
    this.selectedCountry = this.countryCodes.find(c => c.code === 'NO') || this.countryCodes[0];
    this.Form.patchValue({ countryCode: this.selectedCountry });

    this.filteredCountryCodes.next(this.countryCodes.slice());
    this.countryFilterCtrl.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.filterCountryCodes();
      });
  }

  ngOnInit(): void {}

  ngOnDestroy() {
    this.ngUnsubscribe.next(null);
    this.ngUnsubscribe.complete();
  }

  e164PhoneValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const countryCode = control.get('countryCode')?.value as CountryCode;
      const phoneNumber = control.get('phonenumber')?.value;

      if (!countryCode || !phoneNumber) {
        return null;
      }

      const fullPhoneNumber = countryCode.dialCode + phoneNumber;

      if (!this.e164Regex.test(fullPhoneNumber)) {
        return { invalidE164Phone: true };
      }

      return null;
    };
  }

  async create(
    firstname: string,
    lastname: string,
    initials: string,
    role: UserRole,
    title: string,
    email: string,
    countryCode: CountryCode,
    phoneNumber: string
  ) {
    const u: User = {
      firstname,
      lastname,
      initials,
      role,
      title,
      email,
      phone: { locale: countryCode.dialCode, number: phoneNumber },
    };
    const userId = await this.userservice.createUser(u);

    const newUserDocRef = this.afs.doc(`newusers/${userId}`);
    const unsubscribeNewUser = new Subject();

    newUserDocRef.snapshotChanges()
      .pipe(takeUntil(unsubscribeNewUser))
      .subscribe(async (snapshot) => {
        if (!snapshot.payload.exists) {
          // User created successfully
          unsubscribeNewUser.next(null);
          unsubscribeNewUser.complete();
          this.Form.reset();
          this.Form.markAsPristine();
          this.Form.markAsUntouched();
          this.dialogRef.close("USER_CREATED");
        } else {
          const data = snapshot.payload.data() as any;
          if (data?.error) {
            this.error = `Feil ved opprettelse av bruker: ${data.error}`;
            this.snackbarservice.openSnackBar(this.error, '');
            await this.adminservice.buildEventLogItemAndAddToEventLog({
              type: EventLogItemType.USER_CREATION_ERROR,
              source: EventLogSource.APG_USERS,
              user: userId,
              errorMessage: data.error
            });
            unsubscribeNewUser.next(null);
            unsubscribeNewUser.complete();
          }
        }
      });
  }

  private filterCountryCodes() {
    if (!this.countryCodes) {
      return;
    }
    let search = this.countryFilterCtrl.value;
    if (!search) {
      this.filteredCountryCodes.next(this.countryCodes.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    this.filteredCountryCodes.next(
      this.countryCodes.filter(country =>
        country.name.toLowerCase().indexOf(search) > -1 ||
        country.dialCode.indexOf(search) > -1 ||
        country.code.toLowerCase().indexOf(search) > -1
      )
    );
  }

  onlyNumbers(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      event.preventDefault();
      return false;
    }
    return true;
  }
}
